import React, { useState, useEffect, useMemo, useCallback, createContext, useContext } from 'react';
import { FiChevronDown, FiChevronUp, FiX, FiCheckCircle, FiXCircle, FiAlertCircle, FiInfo } from 'react-icons/fi';

// Context pour gérer les toasts globalement
const ToastContext = createContext();

let toastId = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const addToast = useCallback((message, type = 'info', options = {}) => {
    const now = Date.now();
    // Dédupliquer : ignorer si un toast identique existe déjà depuis < 1s
    setToasts(prev => {
      const duplicate = prev.find(t => t.message === message && t.type === type && now - t.timestamp < 1000);
      if (duplicate) return prev;

      const id = ++toastId;
      const newToast = { id, message, type, timestamp: now, autoClose: options.autoClose !== false ? (options.autoClose || 5000) : false };

      if (newToast.autoClose) {
        setTimeout(() => removeToast(id), newToast.autoClose);
      }
      return [...prev, newToast];
    });
    return toastId;
  }, [removeToast]);

  const removeAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const contextValue = useMemo(() => ({ toasts, addToast, removeToast, removeAllToasts }), [toasts, addToast, removeToast, removeAllToasts]);

  // Enregistrer le contexte globalement pour l'objet toast
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__TOAST_CONTEXT__ = contextValue;
    }
  }, [contextValue]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Hook pour remplacer les méthodes toast.* de react-toastify
export const useToastMethods = () => {
  const { addToast } = useToast();

  return {
    success: (message, options) => addToast(message, 'success', options),
    error: (message, options) => addToast(message, 'error', options),
    info: (message, options) => addToast(message, 'info', options),
    warning: (message, options) => addToast(message, 'warning', options),
  };
};

const CollapsedToastContainer = () => {
  const { toasts, removeToast, removeAllToasts } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);

  // Auto-collapse si aucun toast
  useEffect(() => {
    if (toasts.length === 0) {
      setIsExpanded(false);
    }
  }, [toasts.length]);

  const getToastIcon = (type) => {
    switch (type) {
      case 'success': return <FiCheckCircle className="text-green-500" />;
      case 'error': return <FiXCircle className="text-red-500" />;
      case 'warning': return <FiAlertCircle className="text-orange-500" />;
      case 'info': return <FiInfo className="text-blue-500" />;
      default: return <FiInfo className="text-gray-500" />;
    }
  };

  const getToastColor = (type) => {
    switch (type) {
      case 'success': return 'bg-green-50 dark:bg-green-900 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200';
      case 'error': return 'bg-red-50 dark:bg-red-900 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200';
      case 'warning': return 'bg-orange-50 dark:bg-orange-900 border-orange-200 dark:border-orange-800 text-orange-800 dark:text-orange-200';
      case 'info': return 'bg-blue-50 dark:bg-blue-900 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200';
      default: return 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-800 text-gray-800 dark:text-gray-200';
    }
  };

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (toasts.length === 0) return null;

  // Les toasts les plus récents sont à la fin du tableau, on inverse pour afficher les plus récents en premier
  const reversedToasts = [...toasts].reverse();

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm">
      {/* Mode collapsed : toasts empilés avec décalage */}
      {!isExpanded && reversedToasts.length > 1 && (
        <div className="relative">
          {/* Toasts en arrière-plan (effet de pile) */}
          {reversedToasts.slice(1, 4).map((toast, index) => (
            <div
              key={toast.id}
              className={`absolute p-4 rounded-lg border shadow-lg transition-all duration-200 ${
                getToastColor(toast.type)
              }`}
              style={{
                top: `${(index + 1) * 8}px`,
                right: `${(index + 1) * 4}px`,
                width: 'calc(100% - 8px)',
                zIndex: -(index + 1),
                opacity: 0.9 - (index * 0.1)
              }}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className="flex-shrink-0 mt-0.5">
                    {getToastIcon(toast.type)}
                  </div>
                  <p className="text-sm font-medium leading-relaxed truncate">
                    {toast.message}
                  </p>
                </div>
              </div>
            </div>
          ))}
          
          {/* Toast principal au premier plan (le plus récent) */}
          <div 
            className={`relative p-4 rounded-lg border shadow-lg transition-all duration-200 cursor-pointer ${
              getToastColor(reversedToasts[0].type)
            }`}
            onClick={handleToggleExpanded}
            style={{ zIndex: 10 }}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <div className="flex-shrink-0 mt-0.5">
                  {getToastIcon(reversedToasts[0].type)}
                </div>
                <p className="text-sm font-medium leading-relaxed">
                  {reversedToasts[0].message}
                </p>
              </div>
              <div className="flex items-center space-x-2 ml-3">
                <span className="text-xs bg-white dark:bg-gray-800 rounded-full px-2 py-1 font-semibold">
                  {reversedToasts.length}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeToast(reversedToasts[0].id);
                  }}
                  className="text-current opacity-60 hover:opacity-100 transition-opacity flex-shrink-0"
                >
                  <FiX size={16} />
                </button>
                <button 
                  className="text-current opacity-60 hover:opacity-100 transition-opacity flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleExpanded();
                  }}
                >
                  <FiChevronDown size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mode expanded : toasts déroulés (les plus récents en premier) */}
      {isExpanded && (
        <div>
          {reversedToasts.map((toast, index) => (
            <div
              key={toast.id}
              className={`mb-2 p-4 rounded-lg border shadow-lg transition-all duration-200 ${
                getToastColor(toast.type)
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className="flex-shrink-0 mt-0.5">
                    {getToastIcon(toast.type)}
                  </div>
                  <p className={`text-sm leading-relaxed ${
                    index === 0 ? 'font-medium' : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {toast.message}
                  </p>
                </div>
                <div className="flex items-center space-x-2 ml-3">
                  {index === 0 && reversedToasts.length > 1 && (
                    <span className="text-xs bg-white dark:bg-gray-800 rounded-full px-2 py-1 font-semibold">
                      {reversedToasts.length}
                    </span>
                  )}
                  {index === 0 && reversedToasts.length > 1 && (
                    <button
                      onClick={handleToggleExpanded}
                      className="text-current opacity-60 hover:opacity-100 transition-opacity flex-shrink-0"
                    >
                      <FiChevronUp size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => removeToast(toast.id)}
                    className="text-current opacity-60 hover:opacity-100 transition-opacity flex-shrink-0"
                  >
                    <FiX size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
          
          {/* Bouton pour tout fermer */}
          {reversedToasts.length > 1 && (
            <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <button
                onClick={removeAllToasts}
                className="w-full text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 py-1 transition-colors"
              >
                Fermer tous ({reversedToasts.length})
              </button>
            </div>
          )}
        </div>
      )}

      {/* Toast unique (pas de collapse nécessaire) */}
      {reversedToasts.length === 1 && !isExpanded && (
        <div 
          className={`p-4 rounded-lg border shadow-lg transition-all duration-200 ${
            getToastColor(reversedToasts[0].type)
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              <div className="flex-shrink-0 mt-0.5">
                {getToastIcon(reversedToasts[0].type)}
              </div>
              <p className="text-sm font-medium leading-relaxed">
                {reversedToasts[0].message}
              </p>
            </div>
            <button
              onClick={() => removeToast(reversedToasts[0].id)}
              className="text-current opacity-60 hover:opacity-100 transition-opacity flex-shrink-0 ml-3"
            >
              <FiX size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollapsedToastContainer;